import * as React from 'react'
import { InsightWithSource } from '@shared/utils/insights/types'
import { useExternalFocusChatInput, useAttachmentStore, AnimatedLabel } from '@renderer/chat'
import { ActionPopover, Tooltip } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { useInsightActions } from '@renderer/hooks/useInsightActions'
import { useCopyInsightsToClipboard } from '@renderer/hooks/insights.ts'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer.ts'
import {
  MailCheck,
  MessageSquareText,
  ThumbsDown,
  ThumbsUp,
  X,
  Undo2,
  ArrowUp,
  Trash2,
  Copy,
  CopyCheck,
  MailMinus,
} from 'lucide-react'
import { useInsightProperties } from './hooks'

export const InsightPopover = React.memo(
  ({
    children,
    disabled,
    insights,
    offsetY,
    align,
    displayMode,
    onOpen,
  }: React.PropsWithChildren<{
    disabled?: boolean
    align?: 'start' | 'center' | 'end'
    offsetY?: number
    insights: Array<InsightWithSource>
    displayMode: 'grid' | 'list'
    onOpen?: (e: React.MouseEvent) => void
  }>) => {
    // State
    const { onDeleteInsight } = useInsightProperties(insights[0])
    const { bulkDeleteInsights, bulkPatchInsights, setInsightStatus, setInsightSentiment } = useInsightActions()
    const externalFocusInput = useExternalFocusChatInput()
    const { addInsightAttachments } = useAttachmentStore()
    const copyToClipboard = useCopyInsightsToClipboard()

    // Actions state
    const [isAsking, setAsking] = React.useState(false)
    const [askInput, setAskInput] = React.useState('')
    const [isDeleting, setDeleting] = React.useState(false)
    const [showUndo, setShowUndo] = React.useState(false)
    const [copied, setCopied] = React.useState(false)
    const [isHovered, setHovered] = React.useState(false)

    const isActive = isDeleting || isAsking

    const isResolved = React.useMemo(() => {
      return insights[0].status === 'completed'
    }, [insights[0].status])

    const feedbackSentiment = React.useMemo(() => {
      return insights[0].sentimentScore === 1 ? 'positive' : insights[0].sentimentScore === -1 ? 'negative' : 'unset'
    }, [insights[0].sentimentScore])

    const onAsk = (e: React.MouseEvent) => {
      e.stopPropagation()

      // add attachments and focus chat input
      if (typeof onOpen === 'function') {
        onOpen(e)
      } else {
        addInsightAttachments(...insights.map(({ id }) => id))
        externalFocusInput()
      }

      AmplitudeRenderer.trackEvent('Insights Action', {
        action: 'Click ask',
        count: insights.length,
        source: displayMode === 'list' ? 'Stack modal' : 'Grid stack',
      })
    }

    const onResolve = async (e: React.MouseEvent) => {
      e.stopPropagation()
      setShowUndo(true)

      if (insights.length > 1) {
        bulkPatchInsights(
          insights.map(({ id }) => id),
          { status: 'completed' },
        )
      } else {
        setInsightStatus(insights[0].id, 'completed')
      }

      setTimeout(() => {
        setShowUndo(false)
      }, 3000)

      AmplitudeRenderer.trackEvent('Insights Action', {
        action: 'Resolve',
        count: insights.length,
        source: displayMode === 'list' ? 'Stack modal' : 'Grid stack',
      })
    }

    const onUnresolve = async (e: React.MouseEvent) => {
      e.stopPropagation()
      if (insights.length > 1) {
        bulkPatchInsights(
          insights.map(({ id }) => id),
          { status: 'active' },
        )
      } else {
        setInsightStatus(insights[0].id, 'active')
      }
      setShowUndo(false)
    }

    const onDelete = async (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!isDeleting) {
        setDeleting(true)
        return
      }
      if (insights.length === 1) {
        await onDeleteInsight()
      } else {
        bulkDeleteInsights(insights.map((insight) => insight.id))
      }
      setDeleting(false)

      AmplitudeRenderer.trackEvent('Insights Action', {
        action: 'Delete',
        count: insights.length,
        source: displayMode === 'list' ? 'Stack modal' : 'Grid stack',
      })
    }

    const onCopy = (e: React.MouseEvent) => {
      e.stopPropagation()
      copyToClipboard(insights, {
        source: displayMode === 'list' ? 'Stack modal' : 'Grid stack',
      })
      setCopied(true)
      setTimeout(() => {
        setCopied(false)
      }, 1500)
    }

    const onProvideFeedback = (e: React.MouseEvent, rating: 'positive' | 'negative') => {
      e.stopPropagation()

      // @TODO rate all in a stack?
      setInsightSentiment(insights[0].id, rating)

      AmplitudeRenderer.trackEvent('Insights Action', {
        action: 'Rate',
        count: insights.length,
        rating,
        source: displayMode === 'list' ? 'Stack modal' : 'Grid stack',
      })
    }

    return (
      <ActionPopover
        open={disabled ? false : isActive || showUndo || isHovered}
        onOpenChange={(open) => !disabled && !isActive && !showUndo && setHovered(open)}
      >
        <ActionPopover.Trigger>
          {typeof children === 'function'
            ? // @ts-ignore
              children({ isActive })
            : children}
        </ActionPopover.Trigger>
        <ActionPopover.Content
          className={twMerge(isAsking && 'focus-within:outline-light/80')}
          align={align ?? 'end'}
          side={'bottom'}
          alignOffset={-20}
          sideOffset={-20 + (offsetY ?? 0)}
        >
          <ActionPopover.Body>
            <ActionPopover.Screen id={insights[0].id} key="confirm-delete" isActive={isDeleting}>
              <span className={'text-light/80 whitespace-nowrap px-2 text-sm leading-none'}>
                Are you sure you want to delete{' '}
                {insights.length > 1 ? `${insights.length} highlights` : 'this highlight'}?
              </span>
              <ActionPopover.Button
                shape="rounded"
                variant="primary"
                tone="danger"
                onClick={onDelete}
                left={<Trash2 size={16} />}
              >
                Delete
              </ActionPopover.Button>
              <ActionPopover.Button
                onClick={(e) => {
                  e.stopPropagation()
                  setDeleting(false)
                }}
                shape="circle"
              >
                <X size={16} />
              </ActionPopover.Button>
            </ActionPopover.Screen>

            <ActionPopover.Screen id={insights[0].id} key="chat" isActive={isAsking}>
              <textarea
                autoFocus={true}
                rows={1}
                className={'h-fit min-w-96 resize-none whitespace-pre-wrap bg-transparent px-2 text-sm outline-none'}
                placeholder={`Ask about ${insights.length > 1 ? 'these highlights' : 'this highlight'}...`}
                value={askInput}
                onChange={(e) => setAskInput(e.target.value)}
              />
              <ActionPopover.Button onClick={() => setAsking(false)} shape="circle">
                <AnimatedLabel
                  className={'flex'}
                  label={askInput.length ? <ArrowUp key="submit" size={16} /> : <X key="cancel" size={16} />}
                />
              </ActionPopover.Button>
            </ActionPopover.Screen>

            <ActionPopover.Screen id={insights[0].id} key="done" isActive={showUndo}>
              <span className={'text-light/80 whitespace-nowrap px-2 text-sm leading-none'}>All done!</span>
              <ActionPopover.Button
                className={'max-w-fit flex-shrink-0 gap-1.5 rounded-full px-2 text-sm'}
                onClick={(e) => {
                  onUnresolve(e)
                  AmplitudeRenderer.trackEvent('Insights Action', {
                    action: 'Undo resolve',
                    count: insights.length,
                    source: displayMode === 'list' ? 'Stack modal' : 'Grid stack',
                  })
                }}
                left={<Undo2 size={16} />}
              >
                Undo{insights.length > 1 ? ' all' : ''}
              </ActionPopover.Button>
            </ActionPopover.Screen>

            <ActionPopover.Screen id={insights[0].id} key="actions" isActive={!isDeleting && !isAsking && !showUndo}>
              <Tooltip.Provider>
                <Tooltip>
                  <Tooltip.Trigger render={<div className="relative flex" />}>
                    <ActionPopover.Button
                      className={'max-w-fit gap-1.5 rounded-full px-2 text-sm'}
                      onClick={onAsk}
                      left={<MessageSquareText size={16} />}
                    >
                      Ask
                    </ActionPopover.Button>
                  </Tooltip.Trigger>
                  <Tooltip.Content side="bottom" sideOffset={12}>
                    {`Chat about ${insights.length > 1 ? 'these highlights' : 'this highlight'}`}
                  </Tooltip.Content>
                </Tooltip>
                {isResolved ? (
                  <Tooltip>
                    <Tooltip.Trigger render={<div className="relative flex" />}>
                      <ActionPopover.Button
                        className={'max-w-fit gap-1.5 rounded-full px-2 text-sm'}
                        onClick={(e) => {
                          onUnresolve(e)
                          AmplitudeRenderer.trackEvent('Insights Action', {
                            action: 'Unresolve',
                            count: insights.length,
                            source: displayMode === 'list' ? 'Stack modal' : 'Grid stack',
                          })
                        }}
                        left={<MailMinus size={16} />}
                      >
                        Unresolve
                      </ActionPopover.Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content side="bottom" sideOffset={12}>
                      {`Mark ${insights.length > 1 ? 'these highlights' : 'this highlight'} as unresolved`}
                    </Tooltip.Content>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <Tooltip.Trigger render={<div className="relative flex" />}>
                      <ActionPopover.Button onClick={onResolve} left={<MailCheck size={16} />}>
                        Resolve{insights.length > 1 ? ' all' : ''}
                      </ActionPopover.Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content side="bottom" sideOffset={12}>
                      {`Mark ${insights.length > 1 ? 'these highlights' : 'this highlight'} as resolved`}
                    </Tooltip.Content>
                  </Tooltip>
                )}
                <Tooltip>
                  <Tooltip.Trigger render={<div className="relative flex" />}>
                    <ActionPopover.Button onClick={onCopy} className={twMerge(copied && 'opacity-100')} shape="circle">
                      {copied ? <CopyCheck size={16} /> : <Copy size={16} />}
                    </ActionPopover.Button>
                  </Tooltip.Trigger>
                  <Tooltip.Content side="bottom" sideOffset={12}>
                    {`Copy ${insights.length > 1 ? 'as list' : 'to clipboard'}`}
                  </Tooltip.Content>
                </Tooltip>
                <Tooltip>
                  <Tooltip.Trigger render={<div className="relative flex" />}>
                    <ActionPopover.Button onClick={onDelete} shape="circle">
                      <Trash2 size={16} />
                    </ActionPopover.Button>
                  </Tooltip.Trigger>
                  <Tooltip.Content side="bottom" sideOffset={12}>
                    {`Permanently delete ${insights.length > 1 ? 'these highlights' : 'this highlight'}`}
                  </Tooltip.Content>
                </Tooltip>
              </Tooltip.Provider>
              <ActionPopover.Separator />
              <ActionPopover.Button
                onClick={(e) => onProvideFeedback(e, 'positive')}
                disabled={feedbackSentiment === 'positive'}
                shape="circle"
              >
                <ThumbsUp size={16} />
              </ActionPopover.Button>
              <ActionPopover.Button
                onClick={(e) => onProvideFeedback(e, 'negative')}
                disabled={feedbackSentiment === 'negative'}
                shape="circle"
              >
                <ThumbsDown size={16} />
              </ActionPopover.Button>
            </ActionPopover.Screen>
          </ActionPopover.Body>
        </ActionPopover.Content>
      </ActionPopover>
    )
  },
)
InsightPopover.displayName = 'InsightPopover'
